import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import UnavailabilityBox from './calendar-items/UnavailabilityBox';
import OrderBox from './calendar-items/OrderBox';
import EventBox from './calendar-items/EventBox';
import { CalendarItems } from './calendarWrapper/CalendarWrapper';
import { AccountData, EnrichedEventData, OrderData } from '../api';
import NoteBox from './calendar-items/NoteBox';
import { DEFAULT_ORDER_PAGE_COLOR } from '../common/theme/mbTokens';
import { useLanguage } from '../common/GeneralUtils';
import { useAuth } from '../session/InternalAuthProvider';
import { getOrderPage } from '../utils/AccountUtils';
import { buildMonthSummary, SUMMARY_MIN_DAY_ITEMS, toSummaryItems } from './utils/calendarSummary';
import { useOpenDayInAgenda } from './utils/useOpenDayInAgenda';

const MAX_TYPE_ROWS = 5;
const MAX_PHONE_DOTS = 3;

type MonthCalendarCellProps = {
  calendarItems: CalendarItems;
  dateToRender: string;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
};
const MonthCalendarCell = ({ calendarItems, dateToRender, openOrder, openEvent }: MonthCalendarCellProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const openDayInAgenda = useOpenDayInAgenda();

  if (!calendarItems) {
    return null;
  }
  let orders = calendarItems.orders[dateToRender] || [];
  orders = orders.map((o) => ({ ...o, entityType: 'order' }));
  let events = calendarItems.events[dateToRender] || [];
  events = events.map((e) => ({ ...e, entityType: 'event' }));
  // so it can be sorted with all other events
  const unavailabilities = calendarItems.unavailabilities[dateToRender] || [];
  unavailabilities?.map((x) => ({
    ...x,
    time: x.start_time,
  }));
  const notes = (calendarItems.notes[dateToRender] || []).map((n) => ({ ...n, entityType: 'note' }));
  const allItems: any[] = [...orders, ...events, ...unavailabilities, ...notes].sort(
    (a: any, b: any) => a.time - b.time,
  );
  // Orders and sessions are summarised; the summary opens the day in the agenda.
  const summarise =
    !!account?.calendar_display_settings?.summarize_busy_days && orders.length + events.length >= SUMMARY_MIN_DAY_ITEMS;
  const isSummarised = (item: any) => item.entityType === 'order' || item.entityType === 'event';

  const getPageColor = (orderPageId: string) => getOrderPage(account, orderPageId)?.color ?? DEFAULT_ORDER_PAGE_COLOR;

  const renderItem = (item: any) => {
    if (item.entityType === 'order') {
      return <OrderBox key={item.id} order={item as OrderData} viewType="month" openOrder={openOrder} />;
    }
    if (item.entityType === 'event') {
      return <EventBox key={item.id} event={item} viewType="month" openEvent={openEvent} />;
    }
    if (item.entityType === 'note') {
      return <NoteBox key={item.id} note={item} viewType="month" />;
    }
    return <UnavailabilityBox key={item.id} unavailability={item} viewType="month" />;
  };

  const renderSummary = () => {
    const summary = buildMonthSummary(toSummaryItems(orders, events));
    const ariaLabel = t('calendar.summary.slotAria', {
      time: moment(dateToRender).locale(language).format('D MMMM'),
      count: summary.count,
      persons: summary.persons,
    });
    if (isPhone) {
      return (
        <ButtonBase
          aria-label={ariaLabel}
          onClick={() => openDayInAgenda(dateToRender)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
            width: '100%',
            py: 0.5,
            borderRadius: 1,
            '&:hover': { bgcolor: 'mb.ground' },
          }}>
          <Typography component="span" dir="ltr" fontSize="15px" fontWeight={600} lineHeight={1.2}>
            {`×${summary.count}`}
          </Typography>
          <Typography
            component="span"
            dir="ltr"
            fontSize="11px"
            color="text.secondary"
            lineHeight={1.2}
            sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {summary.persons}
            <PersonOutline sx={{ fontSize: 10 }} />
          </Typography>
          <Box sx={{ display: 'flex', gap: '3px', mt: '2px' }}>
            {summary.types.slice(0, MAX_PHONE_DOTS).map((type) => (
              <Box
                key={type.orderPageId}
                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getPageColor(type.orderPageId) }}
              />
            ))}
          </Box>
        </ButtonBase>
      );
    }
    const hasMoreTypes = summary.types.length > MAX_TYPE_ROWS;
    const visibleTypes = hasMoreTypes ? summary.types.slice(0, MAX_TYPE_ROWS - 1) : summary.types;
    return (
      <ButtonBase
        aria-label={ariaLabel}
        onClick={() => openDayInAgenda(dateToRender)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          textAlign: 'start',
          width: 'calc(100% - 16px)',
          marginInline: '8px',
          paddingInline: '4px',
          borderRadius: 1,
          '&:hover': { bgcolor: 'mb.ground' },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '2px',
            height: 16,
            color: 'text.secondary',
          }}>
          <Typography component="span" dir="ltr" fontSize="12px" color="inherit">
            {`×${summary.count} · ${summary.persons}`}
          </Typography>
          <PersonOutline sx={{ fontSize: 12 }} />
        </Box>
        {visibleTypes.map((type) => (
          <Box key={type.orderPageId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 16 }}>
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: getPageColor(type.orderPageId) }}
            />
            <Typography component="span" fontSize="12px" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {getOrderPage(account, type.orderPageId)?.label?.[language]}
            </Typography>
            <Typography component="span" dir="ltr" fontSize="12px" sx={{ flexShrink: 0 }}>
              <Typography component="span" fontSize="inherit" fontWeight={600}>
                {`×${type.count}`}
              </Typography>
              <Typography component="span" fontSize="inherit" color="text.secondary">
                {` · ${type.persons}`}
              </Typography>
            </Typography>
          </Box>
        ))}
        {hasMoreTypes && (
          <Typography component="span" fontSize="12px" color="text.secondary" sx={{ height: 16 }}>
            {t('calendar.summary.more', { count: summary.types.length - (MAX_TYPE_ROWS - 1) })}
          </Typography>
        )}
      </ButtonBase>
    );
  };

  const renderItems = () => {
    if (!summarise) {
      return allItems.map(renderItem);
    }
    return (
      <>
        {renderSummary()}
        {allItems.filter((item) => !isSummarised(item)).map(renderItem)}
      </>
    );
  };

  return <>{renderItems()}</>;
};

export default MonthCalendarCell;
